/**
 * Validation Reason Codes and Descriptions
 *
 * Maps validation reason codes to human-readable descriptions
 * Based on validation-reasons.json
 */

export interface ValidationReasonInfo {
  code: string;
  category:
    | 'deliverable'
    | 'undeliverable'
    | 'risky'
    | 'do_not_send'
    | 'unknown';
  result: 'deliverable' | 'undeliverable' | 'risky' | 'do_not_send' | 'unknown';
  is_valid: boolean;
  status: 'valid' | 'invalid' | 'error';
  description: string;
  user_friendly: string;
  severity: 'success' | 'warning' | 'error';
}

export const VALIDATION_REASONS: Record<string, ValidationReasonInfo> = {
  accepted_email: {
    code: 'accepted_email',
    category: 'deliverable',
    result: 'deliverable',
    is_valid: true,
    status: 'valid',
    description:
      'Email passed all validation checks including SMTP verification. Mailbox exists and accepts mail.',
    user_friendly: 'Email is verified and deliverable',
    severity: 'success',
  },
  invalid_syntax: {
    code: 'invalid_syntax',
    category: 'undeliverable',
    result: 'undeliverable',
    is_valid: false,
    status: 'invalid',
    description:
      "Email address has invalid syntax (doesn't match RFC standards).",
    user_friendly: 'Invalid email format',
    severity: 'error',
  },
  domain_not_found: {
    code: 'domain_not_found',
    category: 'undeliverable',
    result: 'undeliverable',
    is_valid: false,
    status: 'invalid',
    description: 'Domain does not exist (no DNS A or NS records found).',
    user_friendly: 'Domain does not exist',
    severity: 'error',
  },
  no_mx_record: {
    code: 'no_mx_record',
    category: 'undeliverable',
    result: 'undeliverable',
    is_valid: false,
    status: 'invalid',
    description:
      'Domain exists but has no MX (Mail Exchange) records configured.',
    user_friendly: 'Domain has no mail servers',
    severity: 'error',
  },
  mailbox_does_not_exist: {
    code: 'mailbox_does_not_exist',
    category: 'undeliverable',
    result: 'undeliverable',
    is_valid: false,
    status: 'invalid',
    description:
      'SMTP server confirmed the mailbox does not exist (550 response).',
    user_friendly: 'Mailbox does not exist',
    severity: 'error',
  },
  undeliverable_email: {
    code: 'undeliverable_email',
    category: 'undeliverable',
    result: 'undeliverable',
    is_valid: false,
    status: 'invalid',
    description:
      'Email is confirmed undeliverable by third-party validator (Mailgun, ZeroBounce, Hunter).',
    user_friendly: 'Email is undeliverable',
    severity: 'error',
  },
  role_address: {
    code: 'role_address',
    category: 'risky',
    result: 'risky',
    is_valid: true,
    status: 'valid',
    description:
      'Email is a role-based address (e.g., info@, support@, admin@). These are valid but risky for marketing.',
    user_friendly: 'Role-based address (e.g., info@, support@)',
    severity: 'warning',
  },
  smtp_verification_failed: {
    code: 'smtp_verification_failed',
    category: 'risky',
    result: 'risky',
    is_valid: true,
    status: 'valid',
    description:
      'Domain and MX records are valid, but SMTP verification failed (timeout, blocked, or rate-limited). Email is likely valid but not confirmed.',
    user_friendly: 'Domain valid but mailbox not verified',
    severity: 'warning',
  },
  disposable_address: {
    code: 'disposable_address',
    category: 'do_not_send',
    result: 'do_not_send',
    is_valid: false,
    status: 'invalid',
    description:
      'Email is from a disposable/temporary email service (e.g., 10minutemail.com, mailinator.com).',
    user_friendly: 'Disposable email address',
    severity: 'error',
  },
  disposable_email: {
    code: 'disposable_email',
    category: 'do_not_send',
    result: 'do_not_send',
    is_valid: false,
    status: 'invalid',
    description:
      'Email is from a disposable provider (detected by third-party validator).',
    user_friendly: 'Disposable email provider',
    severity: 'error',
  },
  do_not_send_email: {
    code: 'do_not_send_email',
    category: 'do_not_send',
    result: 'do_not_send',
    is_valid: false,
    status: 'invalid',
    description:
      'Email is marked as "do not send" by validator (likely spam trap or complainer).',
    user_friendly: 'Do not send - spam trap or complainer',
    severity: 'error',
  },
  spam_trap: {
    code: 'spam_trap',
    category: 'do_not_send',
    result: 'do_not_send',
    is_valid: false,
    status: 'invalid',
    description: 'Email is identified as a spam trap (ZeroBounce).',
    user_friendly: 'Spam trap email',
    severity: 'error',
  },
  abuse_email: {
    code: 'abuse_email',
    category: 'do_not_send',
    result: 'do_not_send',
    is_valid: false,
    status: 'invalid',
    description: 'Email is an abuse/complaint address (ZeroBounce).',
    user_friendly: 'Abuse/complaint address',
    severity: 'error',
  },
  high_risk_email: {
    code: 'high_risk_email',
    category: 'do_not_send',
    result: 'do_not_send',
    is_valid: false,
    status: 'invalid',
    description: 'Email has high risk score from Mailgun validator.',
    user_friendly: 'High risk email',
    severity: 'error',
  },
  catch_all_domain: {
    code: 'catch_all_domain',
    category: 'unknown',
    result: 'unknown',
    is_valid: false,
    status: 'error',
    description:
      'Domain accepts all emails (catch-all). Cannot verify if specific mailbox exists.',
    user_friendly: 'Catch-all domain (accepts all emails)',
    severity: 'warning',
  },
  smtp_check_failed: {
    code: 'smtp_check_failed',
    category: 'unknown',
    result: 'unknown',
    is_valid: false,
    status: 'error',
    description:
      'SMTP verification failed and domain/MX validation also failed. Complete validation failure.',
    user_friendly: 'SMTP verification failed',
    severity: 'error',
  },
  unknown_status: {
    code: 'unknown_status',
    category: 'unknown',
    result: 'unknown',
    is_valid: false,
    status: 'error',
    description:
      'Validator returned unknown status. Unable to determine validity.',
    user_friendly: 'Unable to determine validity',
    severity: 'warning',
  },
};

/**
 * Get human-readable description for a validation reason code
 * @param reason - The reason code (e.g., "accepted_email", "invalid_syntax")
 * @returns Human-readable description or the reason code if not found
 */
export function getValidationReasonDescription(reason?: string | null): string {
  if (!reason) return 'No reason provided';

  const reasonInfo = VALIDATION_REASONS[reason];
  return reasonInfo?.user_friendly || reason;
}

/**
 * Get full validation reason info
 * @param reason - The reason code
 * @returns Validation reason info or null if not found
 */
export function getValidationReasonInfo(
  reason?: string | null,
): ValidationReasonInfo | null {
  if (!reason) return null;
  return VALIDATION_REASONS[reason] || null;
}

/**
 * Get severity color class for a validation reason
 * @param reason - The reason code
 * @returns Tailwind color class based on severity
 */
export function getValidationReasonSeverityColor(
  reason?: string | null,
): string {
  const reasonInfo = getValidationReasonInfo(reason);
  if (!reasonInfo) return 'text-muted-foreground';

  switch (reasonInfo.severity) {
    case 'success':
      return 'text-[#10b981]';
    case 'warning':
      return 'text-yellow-500';
    case 'error':
      return 'text-red-500';
    default:
      return 'text-muted-foreground';
  }
}

/**
 * Get category badge color for a validation result
 * @param result - The result category (deliverable, undeliverable, risky, do_not_send, unknown)
 * @returns Tailwind color classes for badge
 */
export function getResultCategoryBadgeColor(result?: string | null): string {
  switch (result?.toLowerCase()) {
    case 'deliverable':
      return 'bg-green-500/10 text-green-500 border-green-500/20';
    case 'undeliverable':
      return 'bg-red-500/10 text-red-500 border-red-500/20';
    case 'risky':
      return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
    case 'do_not_send':
      return 'bg-orange-500/10 text-orange-500 border-orange-500/20';
    case 'unknown':
      return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    default:
      return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
  }
}
